import React, { useState, Fragment } from 'react';
import axios from 'axios';
import { Dialog, Transition } from '@headlessui/react';
import { DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import { ButtonSpinner } from './ui/Spinner';

const importSources = [
    { value: 'pokerium', label: 'Pokerium CSV' },
    { value: 'poker_bankroll_tracker', label: 'Poker Bankroll Tracker' },
    { value: 'poker_analytics', label: 'Poker Analytics' },
];

// Reusable Select Component
const Select = ({ label, value, onChange, options }) => (
    <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label}
        </label>
        <select
            value={value}
            onChange={onChange}
            className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 
                     bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                     focus:border-primary-500 dark:focus:border-primary-400 
                     focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
        >
            {options.map(option => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    </div>
);

const ImportSessionsModal = ({ open, onClose, onSuccess }) => {
    const [file, setFile] = useState(null);
    const [source, setSource] = useState('pokerium');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isUploading, setUploading] = useState(false);

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setError('');
        setSuccess('');
    };

    const handleImport = async () => {
        if (!file) {
            setError('Please select a file to import.');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('source', source);

        setUploading(true);
        setError('');
        setSuccess('');

        try {
            await axios.post('/api/import/sessions/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setSuccess('Sessions imported successfully!');
            if (onSuccess) {
                setTimeout(onSuccess, 1500); // Give user time to see success message
            }
        } catch (err) {
            const errorMsg = err.response?.data?.error || 'Failed to import sessions. Please check the file format and try again.';
            setError(errorMsg);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/25 dark:bg-black/40" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden 
                                                   rounded-2xl bg-white dark:bg-gray-800 
                                                   border border-gray-200 dark:border-gray-700
                                                   p-6 text-left align-middle shadow-xl transition-all">
                                <Dialog.Title className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white 
                                                      pb-4 border-b border-gray-200 dark:border-gray-700">
                                    Import Sessions
                                </Dialog.Title>

                                <div className="mt-6 space-y-4">
                                    <Select
                                        label="From which app are you importing?"
                                        value={source}
                                        onChange={(e) => setSource(e.target.value)}
                                        options={importSources}
                                    />

                                    {source === 'pokerium' && (
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Ensure your CSV file matches our format.{' '}
                                            <a 
                                                href="/sample_sessions.csv" 
                                                download="sample_sessions.csv"
                                                className="text-primary-600 dark:text-primary-400 hover:underline"
                                            >
                                                Download sample file
                                            </a>
                                        </p>
                                    )}

                                    <div className="mt-4">
                                        <label className="block w-full rounded-lg border-2 border-dashed
                                                       border-gray-300 dark:border-gray-600 p-4
                                                       text-center hover:border-primary-500 
                                                       dark:hover:border-primary-400 cursor-pointer
                                                       transition-colors">
                                            <DocumentArrowUpIcon className="mx-auto h-8 w-8 text-gray-400" />
                                            <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-white">
                                                {file ? `Selected: ${file.name}` : 'Choose CSV File'}
                                            </span>
                                            <input
                                                type="file"
                                                accept=".csv"
                                                className="hidden"
                                                onChange={handleFileChange}
                                            />
                                        </label>
                                    </div>

                                    {error && (
                                        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/30 
                                                      text-red-700 dark:text-red-400 text-sm">
                                            {error}
                                        </div>
                                    )}

                                    {success && (
                                        <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/30 
                                                      text-green-700 dark:text-green-400 text-sm">
                                            {success}
                                        </div>
                                    )}

                                    <button
                                        onClick={handleImport}
                                        disabled={isUploading || !file}
                                        className="w-full px-4 py-3 text-sm font-medium text-white
                                                 bg-primary-600 hover:bg-primary-700 rounded-lg
                                                 focus:outline-none focus:ring-2 focus:ring-primary-500
                                                 focus:ring-offset-2 disabled:opacity-50
                                                 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {isUploading ? (
                                            <div className="flex items-center justify-center">
                                                <ButtonSpinner />
                                                <span className="ml-2">Importing...</span>
                                            </div>
                                        ) : (
                                            'Import Sessions'
                                        )}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default ImportSessionsModal; 